import { useState, useEffect, useRef, useCallback } from "react";
import JsSIP from "jssip";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Phone, PhoneCall, PhoneOff, PhoneIncoming, Mic, MicOff, Pause, Play,
  Delete, X, Grip, Wifi, WifiOff, Loader2, Dot,
} from "lucide-react";

const STATUS = {
  connecting: { txt: "Conectando...", cls: "text-amber-500", Icon: Loader2, spin: true },
  registered: { txt: "Disponível", cls: "text-emerald-500", Icon: Wifi },
  failed: { txt: "Falha no registro", cls: "text-red-500", Icon: WifiOff },
  disconnected: { txt: "Desconectado", cls: "text-slate-400", Icon: WifiOff },
};

const DIAL_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

export default function SoftPhone() {
  const [open, setOpen] = useState(false);
  const [regState, setRegState] = useState("connecting");
  const [ramal, setRamal] = useState(null);
  const [callState, setCallState] = useState("idle"); // idle|incoming|calling|in-call
  const [numero, setNumero] = useState("");
  const [remoteId, setRemoteId] = useState("");
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [dur, setDur] = useState(0);
  const [showDtmf, setShowDtmf] = useState(false);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const ringRef = useRef(null);

  const startTimer = () => {
    setDur(0);
    timerRef.current = setInterval(() => setDur((d) => d + 1), 1000);
  };
  const stopTimer = () => { clearInterval(timerRef.current); timerRef.current = null; };

  const playRing = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const beep = () => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 480; g.gain.value = 0.08;
        o.start(); o.stop(ctx.currentTime + 0.7);
      };
      beep();
      ringRef.current = { ctx, iv: setInterval(beep, 2000) };
    } catch (e) { /* noop */ }
  };
  const stopRing = () => {
    if (ringRef.current) { clearInterval(ringRef.current.iv); try { ringRef.current.ctx.close(); } catch (e) {} ringRef.current = null; }
  };

  const ringbackRef = useRef(null);
  const establishRef = useRef(null);
  const playRingback = () => {
    stopRingback();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const tone = () => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 425; g.gain.value = 0.10;
        o.start(); o.stop(ctx.currentTime + 1.0);
      };
      tone();
      ringbackRef.current = { ctx, iv: setInterval(tone, 5000) };
    } catch (e) { /* noop */ }
  };
  const stopRingback = () => {
    if (ringbackRef.current) { clearInterval(ringbackRef.current.iv); try { ringbackRef.current.ctx.close(); } catch (e) {} ringbackRef.current = null; }
  };

  const startEstablishWatch = () => {
    clearInterval(establishRef.current);
    establishRef.current = setInterval(() => {
      const s = sessionRef.current;
      if (!s) { clearInterval(establishRef.current); establishRef.current = null; return; }
      if (s.isEnded && s.isEnded()) { resetCall(); return; }
      if (s.isEstablished && s.isEstablished()) {
        stopRing(); stopRingback();
        setCallState("in-call");
        if (!timerRef.current) startTimer();
      }
    }, 400);
  };

  const bindAudio = (pc) => {
    if (!pc) return;
    const attach = () => {
      try {
        const stream = new MediaStream();
        pc.getReceivers().forEach((r) => {
          if (r.track && r.track.kind === "audio") stream.addTrack(r.track);
        });
        if (audioRef.current && stream.getAudioTracks().length) {
          audioRef.current.srcObject = stream;
          audioRef.current.muted = false;
          audioRef.current.volume = 1;
          audioRef.current.play().catch(() => {});
        }
      } catch (e) { /* noop */ }
    };
    pc.addEventListener("track", (ev) => {
      if (audioRef.current && ev.streams && ev.streams[0]) {
        audioRef.current.srcObject = ev.streams[0];
        audioRef.current.muted = false;
        audioRef.current.volume = 1;
        audioRef.current.play().catch(() => {});
      } else {
        attach();
      }
    });
    attach();
  };
  const attachRemote = (session) => { bindAudio(session.connection); };

  const wireSession = (session, dominio) => {
    sessionRef.current = session;
    session.on("peerconnection", (e) => { bindAudio(e.peerconnection); });
    session.on("icecandidate", (e) => {
      if (e.candidate && e.candidate.type === "srflx") { try { e.ready(); } catch (_) {} }
    });
    session.on("progress", () => { if (session.direction === "outgoing") setCallState("calling"); });
    session.on("accepted", () => { stopRing(); stopRingback(); setCallState("in-call"); startTimer(); bindAudio(session.connection); });
    session.on("confirmed", () => { stopRing(); stopRingback(); setCallState("in-call"); if (!timerRef.current) startTimer(); attachRemote(session); });
    session.on("ended", () => resetCall());
    session.on("failed", () => resetCall());
    session.on("hold", () => setHeld(true));
    session.on("unhold", () => setHeld(false));
    session.on("muted", () => setMuted(true));
    session.on("unmuted", () => setMuted(false));
  };

  const resetCall = () => {
    clearInterval(establishRef.current); establishRef.current = null;
    stopRing(); stopRingback(); stopTimer();
    sessionRef.current = null;
    setCallState("idle"); setRemoteId(""); setMuted(false); setHeld(false); setDur(0); setShowDtmf(false);
  };

  const initUA = useCallback(async () => {
    try {
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const uid = usuario.id || usuario._id || usuario.email;
      if (!uid) { setRegState("failed"); return; }
      const { data } = await axios.post(`${API}/voip/softphone/registrar`, {
        usuario_id: String(uid), usuario_nome: usuario.nome || "",
      });
      setRamal(data);
      const socket = new JsSIP.WebSocketInterface(data.ws_url);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: data.sip_uri,
        password: data.senha,
        register: true,
        session_timers: false,
      });
      uaRef.current = ua;
      ua.on("connected", () => setRegState("connecting"));
      ua.on("disconnected", () => setRegState("disconnected"));
      ua.on("registered", () => setRegState("registered"));
      ua.on("unregistered", () => setRegState("disconnected"));
      ua.on("registrationFailed", () => setRegState("failed"));
      ua.on("newRTCSession", (e) => {
        const session = e.session;
        if (sessionRef.current) { try { session.terminate(); } catch (err) {} return; }
        wireSession(session, data.dominio);
        if (session.direction === "incoming") {
          setRemoteId(session.remote_identity?.uri?.user || "Desconhecido");
          setCallState("incoming");
          setOpen(true);
          playRing();
          session.on("failed", () => resetCall());
        }
      });
      ua.start();
    } catch (err) {
      setRegState("failed");
    }
  }, []);

  useEffect(() => {
    initUA();
    return () => { try { uaRef.current && uaRef.current.stop(); } catch (e) {} stopTimer(); stopRing(); };
  }, [initUA]);

  const fazerLigacao = (numOverride) => {
    const ua = uaRef.current;
    const num = typeof numOverride === "string" ? numOverride : numero;
    if (!ua || !num || callState !== "idle") return;
    const alvo = `sip:${String(num).replace(/[^0-9*#]/g, "")}@${ramal.dominio}`;
    setRemoteId(num);
    setCallState("calling");
    playRingback();
    ua.call(alvo, {
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
    });
    startEstablishWatch();
  };

  useEffect(() => {
    const handler = (e) => {
      const num = String(e.detail?.numero || "").replace(/[^0-9*#]/g, "");
      if (!num) return;
      setOpen(true);
      setNumero(num);
      setTimeout(() => fazerLigacao(num), 250);
    };
    window.addEventListener("softphone-dial", handler);
    return () => window.removeEventListener("softphone-dial", handler);
  });

  const atender = () => { stopRing(); sessionRef.current?.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] } }); startEstablishWatch(); };
  const desligar = () => { try { sessionRef.current?.terminate(); } catch (e) {} resetCall(); };
  const toggleMute = () => { const s = sessionRef.current; if (!s) return; muted ? s.unmute() : s.mute(); };
  const toggleHold = () => { const s = sessionRef.current; if (!s) return; held ? s.unhold() : s.hold(); };
  const enviarDtmf = (k) => { if (callState === "in-call") sessionRef.current?.sendDTMF(k); else setNumero((n) => n + k); };

  const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const st = STATUS[regState] || STATUS.disconnected;
  const StIcon = st.Icon;
  const emChamada = callState === "in-call" || callState === "calling";

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-[9999] h-14 w-14 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105 ${callState === "incoming" ? "bg-emerald-500 animate-bounce" : callState === "in-call" ? "bg-emerald-600" : "bg-blue-600"}`}
        data-testid="softphone-fab"
        title="Softphone"
      >
        {callState === "incoming" ? <PhoneIncoming className="h-6 w-6" /> : <Phone className="h-6 w-6" />}
        <span className={`absolute -top-0.5 -right-0.5 ${st.cls}`}><Dot className="h-6 w-6" strokeWidth={6} /></span>
      </button>
    );
  }

  return (
    <>
      <audio ref={audioRef} autoPlay />
      <div className="fixed bottom-5 right-5 z-[9999] w-80 rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-700 overflow-hidden" data-testid="softphone-panel">
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Grip className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-semibold">{ramal?.label || "Softphone"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-xs ${st.cls}`} data-testid="softphone-status">
              <StIcon className={`h-3.5 w-3.5 ${st.spin ? "animate-spin" : ""}`} /> {st.txt}
            </span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="softphone-close"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {callState === "incoming" && (
          <div className="p-5 text-center space-y-3" data-testid="softphone-incoming">
            <p className="text-xs text-slate-400">Chamada recebida</p>
            <p className="text-2xl font-bold">{remoteId}</p>
            <div className="flex justify-center gap-4 pt-1">
              <Button onClick={atender} className="rounded-full h-14 w-14 bg-emerald-600 hover:bg-emerald-700 p-0" data-testid="softphone-atender"><PhoneCall className="h-6 w-6" /></Button>
              <Button onClick={desligar} className="rounded-full h-14 w-14 bg-red-600 hover:bg-red-700 p-0" data-testid="softphone-rejeitar"><PhoneOff className="h-6 w-6" /></Button>
            </div>
          </div>
        )}

        {emChamada && (
          <div className="p-4 space-y-3" data-testid="softphone-emchamada">
            <div className="text-center">
              <p className="text-xl font-bold">{remoteId}</p>
              <p className="text-xs text-slate-400">{callState === "calling" ? "Chamando..." : held ? "Em espera" : fmtDur(dur)}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="ghost" onClick={toggleMute} className={`flex-col h-16 ${muted ? "bg-amber-500/20 text-amber-400" : "text-slate-200"}`} data-testid="softphone-mute">
                {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}<span className="text-[10px] mt-1">{muted ? "Mudo" : "Mutar"}</span>
              </Button>
              <Button variant="ghost" onClick={toggleHold} disabled={callState !== "in-call"} className={`flex-col h-16 ${held ? "bg-blue-500/20 text-blue-400" : "text-slate-200"}`} data-testid="softphone-hold">
                {held ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}<span className="text-[10px] mt-1">{held ? "Retomar" : "Espera"}</span>
              </Button>
              <Button variant="ghost" onClick={() => setShowDtmf((v) => !v)} disabled={callState !== "in-call"} className={`flex-col h-16 ${showDtmf ? "bg-slate-700" : "text-slate-200"}`} data-testid="softphone-dtmf-toggle">
                <Grip className="h-5 w-5" /><span className="text-[10px] mt-1">Teclado</span>
              </Button>
            </div>
            {showDtmf && (
              <div className="grid grid-cols-3 gap-1.5" data-testid="softphone-dtmf-pad">
                {DIAL_KEYS.map((k) => (
                  <button key={k} onClick={() => enviarDtmf(k)} className="py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-lg font-semibold">{k}</button>
                ))}
              </div>
            )}
            <Button onClick={desligar} className="w-full rounded-full bg-red-600 hover:bg-red-700 gap-2" data-testid="softphone-desligar"><PhoneOff className="h-5 w-5" /> Desligar</Button>
          </div>
        )}

        {callState === "idle" && (
          <div className="p-4 space-y-3" data-testid="softphone-discador">
            <input
              value={numero}
              onChange={(e) => setNumero(e.target.value.replace(/[^0-9*#]/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && fazerLigacao()}
              placeholder="Número ou ramal"
              className="w-full bg-slate-800 rounded-lg px-3 py-2.5 text-center text-xl tracking-wider outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="softphone-input"
            />
            <div className="grid grid-cols-3 gap-1.5">
              {DIAL_KEYS.map((k) => (
                <button key={k} onClick={() => setNumero((n) => n + k)} className="py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-xl font-semibold" data-testid={`softphone-key-${k}`}>{k}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setNumero((n) => n.slice(0, -1))} variant="ghost" className="text-slate-300 px-3" data-testid="softphone-backspace"><Delete className="h-5 w-5" /></Button>
              <Button onClick={fazerLigacao} disabled={!numero || regState !== "registered"} className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 gap-2 h-11" data-testid="softphone-ligar">
                <PhoneCall className="h-5 w-5" /> Ligar
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
